import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        maxWidth: '900px',
        width: '95%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    title: {
        margin: 0,
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#111827',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        cursor: 'pointer',
        color: '#6b7280',
        padding: '4px',
        lineHeight: 1,
    },
    body: {
        padding: '1.5rem',
        overflowY: 'auto',
        flex: 1,
    },
    section: {
        marginBottom: '1.5rem',
    },
    label: {
        display: 'block',
        fontWeight: 600,
        color: '#374151',
        marginBottom: '0.5rem',
    },
    textarea: {
        width: '100%',
        minHeight: '200px',
        padding: '12px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontFamily: 'monospace',
        fontSize: '0.9rem',
        resize: 'vertical',
        boxSizing: 'border-box',
    },
    helpToggle: {
        background: 'none',
        border: 'none',
        color: '#3b82f6',
        cursor: 'pointer',
        fontSize: '0.9rem',
        padding: 0,
        textDecoration: 'underline',
    },
    helpSection: {
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1rem',
    },
    helpCommand: {
        marginBottom: '1rem',
    },
    helpCommandName: {
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: '0.25rem',
    },
    helpSyntax: {
        fontFamily: 'monospace',
        backgroundColor: '#e5e7eb',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.85rem',
        display: 'inline-block',
        marginBottom: '0.5rem',
    },
    helpExample: {
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        color: '#059669',
        marginLeft: '1rem',
    },
    resultSection: {
        marginTop: '1rem',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid',
    },
    resultSuccess: {
        backgroundColor: '#ecfdf5',
        borderColor: '#10b981',
    },
    resultError: {
        backgroundColor: '#fef2f2',
        borderColor: '#ef4444',
    },
    resultPartial: {
        backgroundColor: '#fffbeb',
        borderColor: '#f59e0b',
    },
    logList: {
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        maxHeight: '150px',
        overflowY: 'auto',
        margin: '0.5rem 0',
        padding: '0.5rem',
        backgroundColor: '#f3f4f6',
        borderRadius: '4px',
    },
    logItem: {
        marginBottom: '0.25rem',
    },
    logOk: {
        color: '#059669',
    },
    logError: {
        color: '#dc2626',
    },
    footer: {
        padding: '1rem 1.5rem',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem',
    },
    button: {
        padding: '10px 20px',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.9rem',
        transition: 'background-color 0.2s',
    },
    cancelButton: {
        backgroundColor: '#f1f5f9',
        color: '#374151',
    },
    executeButton: {
        backgroundColor: '#3b82f6',
        color: 'white',
    },
    executeButtonDisabled: {
        backgroundColor: '#93c5fd',
        cursor: 'not-allowed',
    },
};

const SpssSyntaxEditor = ({ isOpen, onClose, onSyntaxExecuted }) => {
    const [syntax, setSyntax] = useState('');
    const [showHelp, setShowHelp] = useState(false);
    const [helpData, setHelpData] = useState(null);
    const [executing, setExecuting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (isOpen && !helpData) {
            // Fetch help data
            api.get('/bcm_norms/spss-files/syntax-help/')
                .then(res => setHelpData(res.data))
                .catch(err => console.error('Failed to load syntax help:', err));
        }
    }, [isOpen, helpData]);

    const handleExecute = async () => {
        if (!syntax.trim()) {
            setResult({ status: 'error', error: 'Please enter SPSS syntax to execute.' });
            return;
        }

        setExecuting(true);
        setResult(null);

        try {
            const response = await api.post('/bcm_norms/spss-files/execute-syntax/', { syntax });
            setResult(response.data);

            if (response.data.status === 'success' && onSyntaxExecuted) {
                onSyntaxExecuted();
            }
        } catch (err) {
            setResult({
                status: 'error',
                error: err.response?.data?.error || err.message || 'Failed to execute syntax'
            });
        } finally {
            setExecuting(false);
        }
    };

    const handleClose = () => {
        setSyntax('');
        setResult(null);
        setShowHelp(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={styles.overlay} onClick={handleClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <div style={styles.header}>
                    <h2 style={styles.title}>SPSS Syntax Editor</h2>
                    <button style={styles.closeButton} onClick={handleClose}>&times;</button>
                </div>

                <div style={styles.body}>
                    <div style={styles.section}>
                        <label style={styles.label}>
                            Enter SPSS Syntax
                            <button
                                style={{ ...styles.helpToggle, marginLeft: '1rem' }}
                                onClick={() => setShowHelp(!showHelp)}
                            >
                                {showHelp ? 'Hide Help' : 'Show Help & Examples'}
                            </button>
                        </label>
                        <textarea
                            style={styles.textarea}
                            value={syntax}
                            onChange={e => setSyntax(e.target.value)}
                            placeholder={`* Enter SPSS syntax here. Examples:\n\nRECODE Age (18 thru 25=1)(26 thru 35=2)(36 thru HI=3) INTO Age_Cat.\nVALUE LABELS Age_Cat 1 'Young' 2 'Middle' 3 'Older'.\nCOMPUTE Total_Score = Q1 + Q2 + Q3.\nIF (Country = 'Egypt') Region = 'MENA'.`}
                        />
                    </div>

                    {showHelp && helpData && (
                        <div style={styles.helpSection}>
                            <strong>Supported Commands:</strong>
                            {helpData.supported_commands.map((cmd, idx) => (
                                <div key={idx} style={styles.helpCommand}>
                                    <div style={styles.helpCommandName}>{cmd.command}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                        {cmd.description}
                                    </div>
                                    <code style={styles.helpSyntax}>{cmd.syntax}</code>
                                    <div style={{ marginTop: '0.25rem' }}>
                                        {cmd.examples.slice(0, 2).map((ex, i) => (
                                            <div key={i} style={styles.helpExample}>{ex}</div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
                                <strong>Notes:</strong>
                                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                                    {helpData.general_notes.map((note, i) => (
                                        <li key={i}>{note}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {result && (
                        <div style={{
                            ...styles.resultSection,
                            ...(result.status === 'success' ? styles.resultSuccess :
                                result.status === 'partial' ? styles.resultPartial :
                                styles.resultError)
                        }}>
                            <strong>
                                {result.status === 'success' ? '✓ Success' :
                                 result.status === 'partial' ? '⚠ Partial Success' :
                                 '✗ Error'}
                            </strong>
                            <div style={{ marginTop: '0.5rem' }}>
                                {result.message || result.error}
                            </div>

                            {result.execution_log && result.execution_log.length > 0 && (
                                <div style={styles.logList}>
                                    {result.execution_log.map((log, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                ...styles.logItem,
                                                ...(log.startsWith('OK') ? styles.logOk :
                                                    log.startsWith('ERROR') ? styles.logError : {})
                                            }}
                                        >
                                            {log}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {result.columns_after && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
                                    Columns in data: {result.columns_after.length} | Rows: {result.row_count}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div style={styles.footer}>
                    <button
                        style={{ ...styles.button, ...styles.cancelButton }}
                        onClick={handleClose}
                    >
                        Close
                    </button>
                    <button
                        style={{
                            ...styles.button,
                            ...styles.executeButton,
                            ...(executing ? styles.executeButtonDisabled : {})
                        }}
                        onClick={handleExecute}
                        disabled={executing}
                    >
                        {executing ? 'Executing...' : 'Execute Syntax'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SpssSyntaxEditor;
